import React from 'react';
import { CalendarDays, Clock3, ExternalLink, MapPin } from 'lucide-react';
import { formatPublicEventDate, formatPublicEventTime } from '../hooks/usePublicEvents';

const fallbackImage = 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1200&q=80';

const getDateParts = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    day: new Intl.DateTimeFormat('pt-BR', { day: '2-digit' }).format(date),
    month: new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(date).replace('.', '').toUpperCase()
  };
};

const EventCard = ({ event, compact = false }) => {
  const imageUrl = event.imageUrl || fallbackImage;
  const dateParts = getDateParts(event.startDate);

  return (
    <article className={`flex h-full flex-col overflow-hidden border border-white/10 bg-[#0d163f] shadow-[0_20px_60px_rgba(0,0,0,0.22)] ${compact ? 'rounded-[1.5rem]' : 'rounded-[1.75rem]'}`}>
      <div className={`relative overflow-hidden ${compact ? 'aspect-[16/10]' : 'aspect-[16/9]'}`}>
        <img src={imageUrl} alt={event.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071132] via-[#071132]/18 to-transparent" />

        {dateParts && (
          <div className="absolute left-4 top-4 rounded-2xl border border-white/15 bg-white/95 px-3 py-2 text-center text-[#030B2E] shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
            <p className="text-2xl font-bold leading-none">{dateParts.day}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{dateParts.month}</p>
          </div>
        )}

        <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2">
          <span className="rounded-full border border-[#17E6B3]/30 bg-[#17E6B3]/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">
            {event.status}
          </span>
        </div>
      </div>

      <div className={`flex h-full flex-col ${compact ? 'p-5' : 'p-6'}`}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="text-sm font-medium text-[#E4EEFF]">{event.subtitle}</span>
        </div>

        <h3 className={`${compact ? 'text-xl' : 'text-2xl'} font-bold text-white`}>{event.title}</h3>
        {event.category && (
          <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#7fe7cc]">
            {event.category}
          </p>
        )}
        <p className={`mt-3 text-sm leading-7 text-[#D9E4FF] ${compact ? 'line-clamp-3' : ''}`}>{event.description}</p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.045] p-4 text-sm text-[#D9E4FF]">
          <div className="flex items-center gap-3">
            <CalendarDays size={16} className="text-[#17E6B3]" />
            <span>{formatPublicEventDate(event.startDate)}</span>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Clock3 size={16} className="text-[#17E6B3]" />
            <span>{formatPublicEventTime(event.startDate)}</span>
          </div>
          <div className="mt-3 flex items-start gap-3">
            <MapPin size={16} className="text-[#17E6B3]" />
            <div>
              <p>{event.location}</p>
              {event.address && event.address !== event.location && (
                <p className="mt-1 text-xs text-[#AFC2E8]">{event.address}</p>
              )}
            </div>
          </div>
        </div>

        <a
          href={event.eventUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#143758] px-5 py-3.5 font-semibold text-white transition-colors hover:bg-[#17E6B3] hover:text-[#030B2E]"
        >
          Ver o evento
          <ExternalLink size={16} />
        </a>
      </div>
    </article>
  );
};

export default EventCard;